import { Camera } from '@babylonjs/core/Cameras/camera';
import { PickingInfo } from '@babylonjs/core/Collisions/pickingInfo';
import { Ray } from '@babylonjs/core/Culling/ray';
import { KeyboardEventTypes, KeyboardInfo } from '@babylonjs/core/Events/keyboardEvents';
import { PointerEventTypes } from '@babylonjs/core/Events/pointerEvents';
import { Matrix, Plane, Vector3 } from '@babylonjs/core/Maths/math';
import { VertexBuffer } from '@babylonjs/core/Meshes/buffer';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { Node as babylonNode } from '@babylonjs/core/node';
import { Scene } from '@babylonjs/core/scene';
import { instanceOf } from '@kb3d/models/classes/decorators/model-utils';
import {
    PointerInfoMinimal,
} from '@kb3d/models/classes/handlers';
import { Kb3dManager, getByDynamicId } from '@kb3d/models/classes/kb3d-manager';
import { Kb3dObject } from '@kb3d/models/classes/kb3d-object';
import { MeshNode } from '@kb3d/models/classes/meshes/mesh-node';
import { SpaceNode } from '@kb3d/models/classes/meshes/space-node';
import { KbVector } from '@kb3d/models/classes/primitives/kb-vector';
import { Token } from '@kb3d/models/classes/token';
import { EMPTY, Observable, Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { KbViewer } from '../kb-viewer';
import { KbArcRotateCamera } from './cameras/kb-arc-rotate-camera';
import { Dragger } from './dragger';
import { MeshFaceEngine } from './face-engine';
import { Highlighter } from './highlighter';
import {
    eHandler,
    IDragArgs,
    IDraggable,
    IKb3dFacetSelectArgs,
    IKb3dPickInfo,
    IKb3dPointerArgs,
    IKeyboardArgs,
    Kb3dDragHandler,
    Kb3dPointerHandler,
} from '@kb3d/models/classes/client-enums';

interface INodeHandlers<T> {
    node: SpaceNode;
    handlers: T[];
}

export enum eMarqueeSelectMode {
    Facets,
    Vertices,
}

export interface IMarqueeSelectOptions {
    cullSelection?: boolean; // If true, don't select faces facing away
    onlyOnShiftPress?: boolean;
}

export class Interaction {
    constructor(private kbViewer: KbViewer, private scene: Scene, public highlighter: Highlighter) {
        this.meshFaceEngine = new MeshFaceEngine(kbViewer);

        this.onClick = this.onClick$.asObservable();
        this.onFacetClick = this.onFacetClick$.asObservable();
        this.onDoubleClick = this.onDoubleClick$.asObservable();
        this.onPointerMove = this.onPointerMove$.asObservable();
        this.onPointerDown = this.onPointerDown$.asObservable();
        this.onPointerUp = this.onPointerUp$.asObservable();
        this.onKeyboard = this.onKeyboard$.asObservable();

        this.attachScene(scene);
    }

    attachScene(scene: Scene, utilityLayer = false) {
        scene.onPointerObservable.add(p => {
            const args = this.getKb3dPointerArgs(p);

            switch (p.type) {
                case PointerEventTypes.POINTERDOWN:
                    this.handlePointerDown(args);
                    break;
                case PointerEventTypes.POINTERUP:
                    this.handlePointerUp(args);
                    break;
                case PointerEventTypes.POINTERMOVE:
                    this.handleMove(args);
                    break;
                // case PointerEventTypes.POINTERWHEEL:
                //     console.log("POINTER WHEEL");
                //     break;
                // case PointerEventTypes.POINTERPICK:
                //     console.log("POINTER PICK");
                //     break;
                case PointerEventTypes.POINTERTAP:
                    this.handleClick(args);
                    break;
                case PointerEventTypes.POINTERDOUBLETAP:
                    this.handleDoubleClick(args);
                    break;
            }
        });

        scene.onKeyboardObservable.add(k => {
            const args = this.getKeyboardArgs(k);

            switch (k.type) {
                case KeyboardEventTypes.KEYDOWN:
                    this.handleKeyboard(args);
            }
        });
    }

    /**observables for consumers to subscribe to */
    public onClick: Observable<IKb3dPointerArgs>;
    public onFacetClick: Observable<IKb3dFacetSelectArgs>;
    public onDoubleClick: Observable<IKb3dPointerArgs>;
    public onPointerMove: Observable<IKb3dPointerArgs>;
    public onPointerDown: Observable<IKb3dPointerArgs>;
    public onPointerUp: Observable<IKb3dPointerArgs>;
    public onKeyboard: Observable<IKeyboardArgs>;
    private onClick$ = new Subject<IKb3dPointerArgs>();
    private onFacetClick$ = new Subject<IKb3dFacetSelectArgs>();
    private onDoubleClick$ = new Subject<IKb3dPointerArgs>();
    private onPointerMove$ = new Subject<IKb3dPointerArgs>();
    private onPointerDown$ = new Subject<IKb3dPointerArgs>();
    private onPointerUp$ = new Subject<IKb3dPointerArgs>();
    private onKeyboard$ = new Subject<IKeyboardArgs>();

    private onMarqueeSelect: Subject<IKb3dFacetSelectArgs & { vertexList: number[] }> | null = null;

    public pointerIsDown = false;
    public dragInProgress = false;
    private _dragHandlerActive = false;
    private _dragHandlerHandlers: Kb3dDragHandler[] = [];
    private _dragHandlerNode: SpaceNode | undefined;
    private _dragHandlerStartPos: KbVector | undefined;
    private _dragHandlerPlane: Plane | undefined;
    private readonly meshFaceEngine: MeshFaceEngine;
    private selectionTolerance = 0;
    private marqueeSelectOptions: IMarqueeSelectOptions & {
        mode: eMarqueeSelectMode;
        startX: number;
        startY: number;
        startRay?: Ray;
        selectBox?: HTMLDivElement;
        mesh: Mesh;
    };
    private cameraFacingDirection = new Vector3();

    public setSelectionTolerance(tolerance: number) {
        this.selectionTolerance = tolerance;
    }

    public setSelectionCulling(enabled: boolean) {
        if (this.marqueeSelectOptions) {
            this.marqueeSelectOptions.cullSelection = enabled;
        }
    }

    public enableMarqueeSelect(meshNode: MeshNode, mode: eMarqueeSelectMode, options?: IMarqueeSelectOptions) {
        const mesh = this.kbViewer.getRendererNode(meshNode);

        if (mesh instanceof Mesh) {
            this.onMarqueeSelect = new Subject();

            this.marqueeSelectOptions = {
                cullSelection: false,
                onlyOnShiftPress: true,
                startX: 0,
                startY: 0,
                mode,
                mesh,
                ...(options || {}),
            };

            return this.onMarqueeSelect.asObservable().pipe(
                distinctUntilChanged((prev, curr) => {
                    if (
                        prev.selectedFaces.length !== curr.selectedFaces.length ||
                        prev.vertexList.length !== curr.vertexList.length
                    ) {
                        return false;
                    }
                    for (let i = 0; i < curr.selectedFaces.length; i++) {
                        if (curr.selectedFaces[i] !== prev.selectedFaces[i]) {
                            return false;
                        }
                    }
                    for (let i = 0; i < curr.vertexList.length; i++) {
                        if (curr.vertexList[i] !== prev.vertexList[i]) {
                            return false;
                        }
                    }

                    return true;
                })
            );
        }
        return EMPTY;
    }

    public disableMarqueeSelect() {
        if (this.onMarqueeSelect) {
            this.onMarqueeSelect.complete();
        }
        this.onMarqueeSelect = null;
    }

    /** Do a hit test of the scene using x,y screen pixel coordinates */
    public pick(x: number, y: number, camera?: Camera) {
        const pickInfo = this.scene.pick(x, y, undefined, undefined, camera);
        if (pickInfo && pickInfo.hit) {
            const info = { ...pickInfo } as PickingInfo & IKb3dPickInfo;
            this.fillKb3dPickInfo(pickInfo, info);
            return info;
        }
        return undefined;
    }

    protected handleClickTypeEvent(args: IKb3dPointerArgs, handlerType: eHandler) {
        // For debugging
        // if (args.pickInfo?.ray) {
        //     RayHelper.CreateAndShow(args.pickInfo.ray, this.scene, new Color3(1, 1, 0.1));
        // }

        if (args.targetNode) {
            //bubble up event, calling any handlers that have been applied to the nodes
            let h = this.getNextNodeHandlers<Kb3dPointerHandler>(args.targetNode, handlerType)!;

            while (h) {
                let propagate = false;
                args.sourceNode = h.node;
                for (const handler of h.handlers) {
                    handler(args);
                    if (args.propagate) propagate = true;
                }
                if (!propagate) break;
                h = this.getNextNodeHandlers<Kb3dPointerHandler>(h.node._parent!, handlerType)!;
            }
        } else {
            //there was no clicked mesh, but if there is a handler on the root scene node, we trigger that
            const handlers = this.getHandlerMap(this.kbViewer.sceneNode, handlerType).getByKey(
                this.kbViewer.sceneNode
            ) as Kb3dPointerHandler[];
            if (handlers) {
                args.targetNode = args.sourceNode = this.kbViewer.sceneNode;
                for (const handler of handlers) {
                    handler(args);
                }
            }
        }
    }

    public handleClick(args: IKb3dPointerArgs) {
        this.handleClickTypeEvent(args, eHandler.click);
        // trigger the normal generic observable
        this.onClick$.next(args);

        // if there is an observer on the facet click, then send the facet data there
        if (this.onFacetClick$.observers.length > 0) {
            if (args.pickInfo?.pickedMesh && args.pickInfo.pickedMesh instanceof Mesh) {
                const pickedMesh = args.pickInfo.pickedMesh;

                const selectedTriangle = args.pickInfo.faceId;
                const faceInfo = this.meshFaceEngine.getMeshFaces(pickedMesh);
                if (faceInfo) {
                    let selection: number[] = [];
                    if (this.selectionTolerance >= 0) {
                        selection = this.meshFaceEngine.expandSelection(
                            [selectedTriangle],
                            pickedMesh,
                            this.selectionTolerance
                        );
                    } else {
                        selection = [selectedTriangle];
                    }

                    const facetArgs: IKb3dFacetSelectArgs = {
                        ...args,
                        altKey: args.event.altKey,
                        selectedFaces: selection,
                    };

                    this.onFacetClick$.next(facetArgs);
                }
            }
        }
    }

    protected handleDoubleClick(args: IKb3dPointerArgs) {
        this.handleClickTypeEvent(args, eHandler.doubleClick);
        //trigger the normal generic observable
        this.onDoubleClick$.next(args);
    }

    protected handleMove(args: IKb3dPointerArgs) {
        if (!this.pointerIsDown && this.kbViewer.sceneNode && this.kbViewer.sceneNode.enableHoverEffects) {
            if (args.targetNode) {
                const clicks = this.getNextClickHandlers(args.targetNode);
                const draggables = this.getNextDraggables(args.targetNode);
                if (clicks || draggables) {
                    const n = clicks?.node || (draggables?.node as SpaceNode);
                    this.highlighter.hover(n);
                }
            } else {
                this.highlighter.clearHover();
            }
        }

        if (this.scene.activeCamera instanceof KbArcRotateCamera && this.pointerIsDown && this.onMarqueeSelect) {
            if (!this.marqueeSelectOptions.onlyOnShiftPress || args.shiftKey) {
                args.event.preventDefault();
                (args.event as MouseEvent).stopPropagation();
                this.handleMarquee(args, this.scene.activeCamera);
            } else {
                this.unlockCamera();
            }
        }

        if (this.pointerIsDown && this._dragHandlerActive && this._dragHandlerPlane && this._dragHandlerNode && this._dragHandlerStartPos && args.pickInfo?.ray) {
            const distance = args.pickInfo.ray.intersectsPlane(this._dragHandlerPlane);
            if (distance != null) {
                const newPosVec = args.pickInfo.ray.direction.scale(distance).add(args.pickInfo.ray.origin);
                const newPos = KbVector.FromVec3(newPosVec);
                const node = this._dragHandlerNode;

                // Do a targeted pick against the drag node's renderer mesh to get accurate texture UVs.
                const rendererMesh = this.kbViewer.getRendererNode(node);
                const uvPick = this.scene.pickWithRay(args.pickInfo.ray, m => m === rendererMesh || m.parent === rendererMesh);
                const tc = (uvPick?.hit) ? uvPick.getTextureCoordinates() : null;
                const u = (tc && isFinite(tc.x)) ? tc.x : 0;
                const v = (tc && isFinite(tc.y)) ? tc.y : 0;

                const dragArgs: IDragArgs = {
                    dragNode: node,
                    newPos,
                    startPos: this._dragHandlerStartPos,
                    allow: true,
                    u,
                    v,
                    newRotationEuler: node.eulerRotation?.clone() || KbVector.Zero(),
                    newRotationAxis: node.rotationAxis?.clone() || KbVector.Zero(),
                    newRotationAngle: node.rotationAngle || 0,
                };
                for (const handler of this._dragHandlerHandlers) {
                    handler(dragArgs);
                }
            }
        }

        this.onPointerMove$.next(args);
    }

    public handlePointerDown(args: IKb3dPointerArgs) {
        this.pointerIsDown = true;

        if (this.onMarqueeSelect) {
            if (!this.marqueeSelectOptions.onlyOnShiftPress || args.shiftKey) {
                this.marqueeSelectOptions.startX = args.canvasX || 0;
                this.marqueeSelectOptions.startY = args.canvasY || 0;
                this.marqueeSelectOptions.startRay =
                    this.scene.pick(args.canvasX!, args.canvasY!, undefined, undefined, this.scene.activeCamera)?.ray ||
                    undefined;

                if (this.kbViewer.uiOverlay) {
                    const selectBox = document.createElement('div');
                    selectBox.style.position = 'absolute';
                    selectBox.style.border = '1px dashed white';
                    selectBox.style.background = 'rgba(128, 128, 128, 0.1)';
                    selectBox.style.opacity = '0.5';
                    this.kbViewer.uiOverlay.appendChild(selectBox);
                    this.marqueeSelectOptions.selectBox = selectBox;
                }

                this.cameraFacingDirection = this.scene.activeCamera!.getForwardRay().direction;
                if (
                    !this.marqueeSelectOptions.mesh.isFacetDataEnabled &&
                    this.marqueeSelectOptions.mode === eMarqueeSelectMode.Facets
                ) {
                    const normals = this.marqueeSelectOptions.mesh.getVerticesData(VertexBuffer.NormalKind);
                    if (!normals) {
                        this.marqueeSelectOptions.mesh.setVerticesData(
                            VertexBuffer.NormalKind,
                            new Float32Array(this.marqueeSelectOptions.mesh.getTotalVertices() * 3)
                        );
                    }
                }
            }
        } else if (args.targetNode) {
            const d = this.getNextDraggables(args.targetNode);
            if (d) {
                this.highlighter.clearHover();
                args.sourceNode = d.node;
                const dragger = new Dragger(this.kbViewer, this.scene);
                this.dragInProgress = true;
                dragger.startDrag(args, d.handlers[0], () => {
                    this.dragInProgress = false;
                });
            }

            if (!this.dragInProgress) {
                const dh = this.getNextDragHandlers(args.targetNode);
                if (dh && args.pickedPoint) {
                    this._dragHandlerNode = dh.node;
                    this._dragHandlerHandlers = dh.handlers;
                    this._dragHandlerStartPos = args.pickedPoint;
                    const cameraPos = this.scene.activeCamera!.position;
                    const cameraDir = cameraPos.subtract(args.pickedPoint.toVec3());
                    this._dragHandlerPlane = Plane.FromPositionAndNormal(args.pickedPoint.toVec3(), cameraDir);
                    this._dragHandlerActive = true;
                }
            }
        }

        this.onPointerDown$.next(args);
    }

    protected unlockCamera() {
        this.scene.activeCamera?.attachControl(this.kbViewer.canvas!);
    }

    public handlePointerUp(args: IKb3dPointerArgs) {
        this.pointerIsDown = false;
        this._dragHandlerActive = false;
        this._dragHandlerHandlers = [];
        this._dragHandlerNode = undefined;
        this._dragHandlerStartPos = undefined;
        this._dragHandlerPlane = undefined;
        if (this.marqueeSelectOptions && this.marqueeSelectOptions.selectBox) {
            this.kbViewer.uiOverlay?.removeChild(this.marqueeSelectOptions.selectBox);
            delete this.marqueeSelectOptions.selectBox;
        }
        this.unlockCamera();

        this.onPointerUp$.next(args);
    }

    protected handleKeyboard(args: IKeyboardArgs) {
        this.onKeyboard$.next(args);
    }

    protected handleMarquee(args: IKb3dPointerArgs, activeCamera: KbArcRotateCamera) {
        const mouseX = args.canvasX || 0;
        const mouseY = args.canvasY || 0;

        activeCamera.detachControl();

        if (this.marqueeSelectOptions.selectBox) {
            this.marqueeSelectOptions.selectBox.style.left = Math.min(this.marqueeSelectOptions.startX, mouseX) + 'px';
            this.marqueeSelectOptions.selectBox.style.top = Math.min(this.marqueeSelectOptions.startY, mouseY) + 'px';
            this.marqueeSelectOptions.selectBox.style.width =
                Math.abs(this.marqueeSelectOptions.startX - mouseX) + 'px';
            this.marqueeSelectOptions.selectBox.style.height =
                Math.abs(this.marqueeSelectOptions.startY - mouseY) + 'px';
        }
        let topOrigin: Vector3;
        let leftOrigin: Vector3;
        let rightOrigin: Vector3;
        let bottomOrigin: Vector3;
        let topNormal: Vector3 | undefined;
        let leftNormal: Vector3 | undefined;
        let rightNormal: Vector3 | undefined;
        let bottomNormal: Vector3 | undefined;
        const m = Matrix.Identity();
        const boxL = Math.min(this.marqueeSelectOptions.startX, mouseX);
        const boxR = Math.max(this.marqueeSelectOptions.startX, mouseX);
        const boxT = Math.min(this.marqueeSelectOptions.startY, mouseY);
        const boxB = Math.max(this.marqueeSelectOptions.startY, mouseY);

        if (activeCamera.mode === Camera.ORTHOGRAPHIC_CAMERA) {
            // ORTHOGRAPHIC CAMERA
            const forwardDirection = activeCamera.getForwardRay().direction;
            const sideDirection = Vector3.Cross(forwardDirection, Vector3.Up());
            topNormal = Vector3.Cross(forwardDirection, sideDirection);
            leftNormal = sideDirection;
            rightNormal = sideDirection.scale(-1);
            bottomNormal = Vector3.Cross(sideDirection, forwardDirection);
            topOrigin = this.scene.createPickingRay(boxL, boxT, m, activeCamera).origin;
            leftOrigin = this.scene.createPickingRay(boxL, boxT, m, activeCamera).origin;
            rightOrigin = this.scene.createPickingRay(boxR, boxB, m, activeCamera).origin;
            bottomOrigin = this.scene.createPickingRay(boxR, boxB, m, activeCamera).origin;
        } else {
            // PERSPECTIVE CAMERA
            // Assemble the 4 planes of the marquee select
            const origin = this.marqueeSelectOptions.startRay?.origin || activeCamera.position;
            topOrigin = origin;
            leftOrigin = origin;
            rightOrigin = origin;
            bottomOrigin = origin;

            const topLeft = this.scene.createPickingRay(boxL, boxT, m, activeCamera).direction;
            const topRight = this.scene.createPickingRay(boxR, boxT, m, activeCamera).direction;
            const botLeft = this.scene.createPickingRay(boxL, boxB, m, activeCamera).direction;
            const botRight = this.scene.createPickingRay(boxR, boxB, m, activeCamera).direction;

            if (topLeft && topRight && botLeft && botRight) {
                // Normals of the planes are equal to the cross product of the two vectors that define the plane
                topNormal = Vector3.Cross(topLeft, topRight);
                leftNormal = Vector3.Cross(botLeft, topLeft);
                rightNormal = Vector3.Cross(topRight, botRight);
                bottomNormal = Vector3.Cross(botRight, botLeft);
            }
        }

        if (topNormal && leftNormal && rightNormal && bottomNormal) {
            const mesh = this.marqueeSelectOptions.mesh;
            const fP = new Vector3();
            const fN = new Vector3();

            if (this.marqueeSelectOptions.mode === eMarqueeSelectMode.Facets) {
                const faceInfo = this.meshFaceEngine.getMeshFaces(mesh)!;
                const transform = mesh.computeWorldMatrix();

                const n = faceInfo.faceCount;
                const res = [] as number[];

                for (let i = 0; i < n; i++) {
                    Vector3.TransformCoordinatesFromFloatsToRef(
                        faceInfo.facePositions[i * 3],
                        faceInfo.facePositions[i * 3 + 1],
                        faceInfo.facePositions[i * 3 + 2],
                        transform,
                        fP
                    );
                    if (this.marqueeSelectOptions.cullSelection) {
                        Vector3.TransformCoordinatesFromFloatsToRef(
                            faceInfo.faceNormals[i * 3],
                            faceInfo.faceNormals[i * 3 + 1],
                            faceInfo.faceNormals[i * 3 + 2],
                            transform,
                            fN
                        );
                    }
                    if (
                        (!this.marqueeSelectOptions.cullSelection || Vector3.Dot(this.cameraFacingDirection, fN) > 0) &&
                        pointPlaneSizeToRef(topNormal, topOrigin, fP) >= 0 &&
                        pointPlaneSizeToRef(leftNormal, leftOrigin, fP) >= 0 &&
                        pointPlaneSizeToRef(rightNormal, rightOrigin, fP) >= 0 &&
                        pointPlaneSizeToRef(bottomNormal, bottomOrigin, fP) >= 0
                    ) {
                        res.push(i);
                    }
                }

                this.onMarqueeSelect!.next({
                    ...args,
                    altKey: args.event.altKey,
                    vertexList: [],
                    selectedFaces: res,
                });
            } else if (this.marqueeSelectOptions.mode === eMarqueeSelectMode.Vertices) {
                const vertexPositions = mesh.getVerticesData(VertexBuffer.PositionKind);
                const transform = mesh.getWorldMatrix();
                const res = [] as number[];

                if (vertexPositions) {
                    const n = vertexPositions.length / 3;
                    for (let i = 0; i < n; i++) {
                        fP.copyFromFloats(
                            vertexPositions[i * 3],
                            vertexPositions[i * 3 + 1],
                            vertexPositions[i * 3 + 2]
                        );
                        Vector3.TransformCoordinatesToRef(fP, transform, fP);

                        if (
                            (!this.marqueeSelectOptions.cullSelection ||
                                Vector3.Dot(this.cameraFacingDirection, mesh.getFacetNormal(i))) &&
                            pointPlaneSizeToRef(topNormal, topOrigin, fP) >= 0 &&
                            pointPlaneSizeToRef(leftNormal, leftOrigin, fP) >= 0 &&
                            pointPlaneSizeToRef(rightNormal, rightOrigin, fP) >= 0 &&
                            pointPlaneSizeToRef(bottomNormal, bottomOrigin, fP) >= 0
                        ) {
                            res.push(i);
                        }
                    }

                    mesh.getIndices();
                    this.onMarqueeSelect!.next({
                        ...args,
                        altKey: args.event.altKey,
                        vertexList: res,
                        selectedFaces: [],
                    });
                }
            }
        }

        function pointPlaneSizeToRef(normal: Vector3, origin: Vector3, point: Vector3) {
            return normal.x * (point.x - origin.x) + normal.y * (point.y - origin.y) + normal.z * (point.z - origin.z);
        }
    }

    public getKb3dPointerArgs(p: PointerInfoMinimal) {
        const args = {
            ...p,
            pickInfo: p.pickInfo,
            propagate: false,
            pointerType: (p.event as any).pointerType,
            button: p.event.button,
            ctrlKey: p.event.ctrlKey || p.event.metaKey,
            shiftKey: p.event.shiftKey,
            canvasX: p.event.offsetX,
            canvasY: p.event.offsetY,
            getNormal: () => {
                if (p.pickInfo) {
                    const normal = p.pickInfo.getNormal();
                    return normal ? KbVector.FromVec3(normal) : undefined;
                }
                return undefined;
            },
        } as IKb3dPointerArgs;

        if (p.pickInfo) {
            this.fillKb3dPickInfo(p.pickInfo, args);
        }
        return args;
    }

    protected fillKb3dPickInfo(b: PickingInfo, k: IKb3dPickInfo) {
        k.u = b.bu;
        k.v = b.bv;
        if (b.pickedPoint) k.pickedPoint = KbVector.FromVec3(b.pickedPoint);
        if (b.pickedMesh) {
            // recurse up the tree until we find something, or return undefined
            let pickedMesh: babylonNode = b.pickedMesh;
            k.targetNode = getByDynamicId(pickedMesh.id);
            while (!k.targetNode && pickedMesh.parent) {
                pickedMesh = pickedMesh.parent;
                k.targetNode = getByDynamicId(pickedMesh.id);
            }

            if (b.pickedPoint) {
                k.meshPoint = KbVector.FromVec3(
                    Vector3.TransformCoordinates(b.pickedPoint, b.pickedMesh.getWorldMatrix().clone().invert())
                );
            }
        }
    }

    protected getKeyboardArgs(k: KeyboardInfo) {
        const event = k.event as unknown as KeyboardEvent;
        const args = {
            ...k,
            key: event.key,
            keyCode: event.keyCode,
            altKey: event.altKey,
            ctrlKey: event.ctrlKey || event.metaKey,
            shiftKey: event.shiftKey,
            repeat: event.repeat,
        } as IKeyboardArgs;

        return args;
    }

    /**
     * scans the given node and ancestors to find the first node with a click handler and returns
     * the node and it's handlers
     */
    protected getNextNodeHandlers<T>(node: SpaceNode, handlerType: eHandler): INodeHandlers<T> | undefined {
        //bubble up event, calling any handlers that have been applied to the nodes
        let handlers = new Array<any>();
        while (node && node._manager) {
            const hmap = this.getHandlerMap(node, handlerType);
            handlers = hmap.getByKey(node);
            //if this is a scene node, then we also have to look in the parent scene manager for click events on this item
            if (instanceOf(node, Token.SceneNode) && node.isNested() && node._parent && node._parent._manager) {
                handlers.push(...this.getHandlerMap(node._parent, handlerType).getByKey(node));
            }

            if (handlers && handlers.length) {
                break;
            }

            node = node._parent as SpaceNode;
        }

        return node ? { node, handlers } : undefined;
    }

    protected getHandlerMap(node: Kb3dObject, type: eHandler) {
        const m = node._manager as Kb3dManager;
        if (type == eHandler.click) return m.clickHandlers;
        else if (type == eHandler.draggable) return m.draggables;
        else if (type == eHandler.doubleClick) return m.doubleClickHandlers;
        else if (type == eHandler.drag) return m.dragHandlers;
        else throw 'unrecognized handler type';
    }

    protected getNextClickHandlers(node: SpaceNode) {
        return this.getNextNodeHandlers<Kb3dPointerHandler>(node, eHandler.click);
    }

    protected getNextDoubleClickHandlers(node: SpaceNode) {
        return this.getNextNodeHandlers<Kb3dPointerHandler>(node, eHandler.doubleClick);
    }

    protected getNextDragHandlers(node: SpaceNode) {
        return this.getNextNodeHandlers<Kb3dDragHandler>(node, eHandler.drag);
    }

    protected getNextDraggables(node: SpaceNode) {
        return this.getNextNodeHandlers<IDraggable>(node, eHandler.draggable);
    }
}
